/**
 * What a `draft_pdf_changes` call adds under its row: the counts, and every drafted item for the
 * attorney to read before the agent applies it with `edit_pdf(batch_id)`.
 * A batch still drafting in the background is polled until it is ready.
 */
import { ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import {
  FilesystemAPI,
  type PdfEditBatch,
  type PdfEditBatchItem
} from "../../../caseFiles/api/filesystemApi";

const POLL_MS = 3000;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

/** The batch a tool call drafted, or null when it drafted none. */
export const pdfBatchIdOf = (
  toolName: string,
  output: unknown
): string | null => {
  if (toolName !== "draft_pdf_changes") return null;
  if (!isRecord(output) || output.error) return null;
  if (isRecord(output.draft)) return str(output.draft.batch_id);
  if (isRecord(output.run) && output.run.kind === "pdf_draft")
    return str(output.run.id);
  return null;
};

const usePdfEditBatch = (
  individualId: string,
  batchId: string
): { batch: PdfEditBatch | null; failed: boolean } => {
  const [batch, setBatch] = useState<PdfEditBatch | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const next = await FilesystemAPI.getPdfEditBatch(individualId, batchId);
        if (cancelled) return;
        setBatch(next);
        if (next.status === "running") timer = setTimeout(tick, POLL_MS);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [batchId, individualId]);

  return { batch, failed };
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "27 drafted · 2 failed · 1 skipped" from the batch's counts. */
export const pdfBatchCountsText = (batch: PdfEditBatch): string => {
  const items = batch.items ?? [];
  const tally = (status: PdfEditBatchItem["status"]) =>
    items.filter((i) => i.status === status).length;
  const ok = tally("ok");
  const verb = batch.status === "applied" ? "applied" : "drafted";
  return [
    `${plural(ok, "item")} ${verb}`,
    tally("failed") ? `${tally("failed")} failed` : null,
    tally("skipped") ? `${tally("skipped")} skipped` : null
  ]
    .filter(Boolean)
    .join(" · ");
};

const ItemRow: FC<{ item: PdfEditBatchItem }> = ({ item }) => {
  const name = item.exhibit_label
    ? `Ex. ${item.exhibit_label}${item.title ? ` · ${item.title}` : ""}`
    : item.title ?? item.node_id;
  const body =
    item.status === "ok"
      ? item.text ?? (item.phrase ? `“${item.phrase}”` : null)
      : [item.phrase ? `“${item.phrase}”` : null, item.detail]
          .filter(Boolean)
          .join(": ");
  return (
    <li
      className="flex min-w-0 flex-col gap-0.5 py-1"
      data-testid="pdf-draft-item"
    >
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate font-medium text-foreground">{name}</span>
        {item.page ? (
          <span className="shrink-0 text-muted-foreground">p. {item.page}</span>
        ) : null}
        {item.status !== "ok" ? (
          <span
            className={cn(
              "shrink-0",
              item.status === "failed"
                ? "text-destructive"
                : "text-muted-foreground"
            )}
          >
            {item.status}
          </span>
        ) : null}
      </div>
      {body ? (
        <div className="text-muted-foreground whitespace-pre-wrap">{body}</div>
      ) : null}
    </li>
  );
};

type Props = {
  individualId: string;
  batchId: string;
};

export const PdfDraftCard: FC<Props> = ({ individualId, batchId }) => {
  const { batch, failed } = usePdfEditBatch(individualId, batchId);
  const [open, setOpen] = useState(false);

  if (failed)
    return (
      <div className="text-xs text-muted-foreground">
        Couldn't load the drafted items.
      </div>
    );
  if (!batch || batch.status === "running")
    return (
      <div
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="pdf-draft-running"
      >
        <Loader2 className="size-3 animate-spin" /> Drafting the items…
      </div>
    );
  if (batch.status === "failed")
    return (
      <div className="text-xs text-destructive">
        Drafting failed{batch.error ? `: ${batch.error}` : "."}
      </div>
    );

  const items = [...(batch.items ?? [])].sort(
    (a, b) => Number(a.status !== "ok") - Number(b.status !== "ok")
  );
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-background px-2.5 py-1.5 text-xs"
      data-testid="pdf-draft-card"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-1 text-left text-muted-foreground hover:text-foreground">
        <ChevronRight
          className={cn("size-3 transition-transform", open && "rotate-90")}
        />
        <span data-testid="pdf-draft-counts">{pdfBatchCountsText(batch)}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {batch.instruction ? (
          <div className="pt-1 text-xs text-muted-foreground italic">
            {batch.instruction}
          </div>
        ) : null}
        <ul className="max-h-80 divide-y overflow-y-auto text-xs">
          {items.map((item, index) => (
            <ItemRow key={`${item.node_id}-${index}`} item={item} />
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};
